import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  EmptyState,
  PageHeader,
  STATUS_LABELS,
  StatusBadge,
} from "../../components/dashboard/DashboardUI";
import ProcedureResponseModal from "../../components/modals/ProcedureResponseModal";
import RequestDetail from "../../components/modals/RequestDetail";
import { fetchAdminProcedures } from "../../redux/actions/ProcedureActions";
import { REQUEST_TYPES, requestTitle } from "../../utils/requestDisplay";

const STATUS_FILTERS = [
  "paid",
  "in_progress",
  "fulfilled",
  "failed",
  "initiated",
];
const PAGE_SIZE = 25;

const fullName = (person) =>
  person && typeof person === "object"
    ? [person.firstname, person.lastname].filter(Boolean).join(" ")
    : "";

const clientName = (request) =>
  fullName(request.user) || request.company?.businessName || "—";

const operatorName = (request) =>
  fullName(request.operator) || (request.operator ? "Attribuée" : "—");

// All client requests, read only
function AdminProcedures() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const { procedures } = useSelector((state) => state.procedures);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState(null);
  const [showResponse, setShowResponse] = useState(false);

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    Promise.resolve(dispatch(fetchAdminProcedures(user.token))).finally(() =>
      setLoading(false),
    );
  }, [user?.token, dispatch]);

  const requests = [...(procedures || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );
  const types = [...new Set(requests.map((request) => request.type))];

  const term = search.trim().toLowerCase();
  const filtered = requests.filter(
    (request) =>
      // Unpaid orders stay hidden unless explicitly requested
      (status ? request.status === status : request.status !== "initiated") &&
      (!type || request.type === type) &&
      (!term ||
        request.reference?.toLowerCase().includes(term) ||
        clientName(request).toLowerCase().includes(term) ||
        requestTitle(request).toLowerCase().includes(term)),
  );

  const waiting = requests.filter((request) => request.status === "paid");

  const resetPaging = (setter) => (event) => {
    setter(event.target.value);
    setVisible(PAGE_SIZE);
  };

  return (
    <>
      <PageHeader
        title="Démarches clients"
        lead={
          waiting.length
            ? `${waiting.length} démarche${waiting.length > 1 ? "s" : ""} payée${
                waiting.length > 1 ? "s" : ""
              } pas encore prise${waiting.length > 1 ? "s" : ""} en charge.`
            : "Toutes les démarches payées sont prises en charge."
        }
      />

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher une référence, un client ou une démarche"
            value={search}
            onChange={resetPaging(setSearch)}
            aria-label="Rechercher"
          />
          <select
            value={type}
            onChange={resetPaging(setType)}
            aria-label="Type de démarche"
          >
            <option value="">Tous les types</option>
            {types.map((value) => (
              <option key={value} value={value}>
                {REQUEST_TYPES[value] || value}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={resetPaging(setStatus)}
            aria-label="Statut"
          >
            <option value="">Payées, en cours, terminées…</option>
            {STATUS_FILTERS.map((value) => (
              <option key={value} value={value}>
                {STATUS_LABELS[value]}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="dash-loading">Chargement des démarches…</p>
        ) : filtered.length === 0 ? (
          <EmptyState title="Aucune démarche">
            Aucune démarche ne correspond à ces critères.
          </EmptyState>
        ) : (
          <>
            <div className="dash-table-wrap">
              <table className="dash-table is-responsive">
                <thead>
                  <tr>
                    <th>Démarche</th>
                    <th>Client</th>
                    <th>Référence</th>
                    <th>Date</th>
                    <th>Opérateur</th>
                    <th>Statut</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, visible).map((request) => (
                    <tr key={request.reference}>
                      <td>
                        <span className="dash-cell-title">
                          {requestTitle(request)}
                        </span>
                      </td>
                      <td data-label="Client">{clientName(request)}</td>
                      <td data-label="Référence">#{request.reference}</td>
                      <td data-label="Date">
                        {moment(request.createdAt).format("DD/MM/YYYY")}
                      </td>
                      <td data-label="Opérateur">{operatorName(request)}</td>
                      <td data-label="Statut">
                        <StatusBadge status={request.status} />
                      </td>
                      <td>
                        <div className="dash-row-actions">
                          {request.comment && (
                            <button
                              type="button"
                              className="dash-link-button"
                              onClick={() => {
                                setSelected(request);
                                setShowResponse(true);
                              }}
                            >
                              Réponse
                            </button>
                          )}
                          <button
                            type="button"
                            className="dash-link-button"
                            onClick={() => setSelected(request)}
                          >
                            Voir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length > visible && (
              <div className="dash-more">
                <button
                  type="button"
                  className="dash-button is-secondary"
                  onClick={() => setVisible((count) => count + PAGE_SIZE)}
                >
                  Afficher plus
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {!showResponse && (
        <RequestDetail request={selected} onHide={() => setSelected(null)} />
      )}

      <ProcedureResponseModal
        show={showResponse}
        onHide={() => {
          setShowResponse(false);
          setSelected(null);
        }}
        procedure={selected}
      />
    </>
  );
}

export default AdminProcedures;
